import { generateShortToken } from '@/helpers/encrypt'
import { LOGIN_ERROR_MESSAGE } from '@/helpers/errorHandler'
import { isTemporaryLiveTrackPage } from '@/helpers/public-tracking'
import { Cookie } from '@/mixin/local-cookie'
import { ApolloClient, HttpLink, InMemoryCache } from '@apollo/client/core'
import { onError } from '@apollo/client/link/error'
import Vue from 'vue'

// Handler for Error Unauthenticated
const errorHandler = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors !== undefined) {
    graphQLErrors.forEach(function ({ message, locations, path, extensions }) {
      const isAuthError =
        extensions &&
        (extensions.code == 'UNAUTHENTICATED' ||
          extensions.code == 'INTERNAL_SERVER_ERROR' ||
          message === LOGIN_ERROR_MESSAGE)

      // 🔥 CEK TOKEN LANGSUNG DARI COOKIE
      const cookie = Cookie.getLocalCookie()
      const hasToken = cookie && cookie.resource

      // Jika belum login, DIAMKAN
      if (!hasToken) {
        return
      }

      // Pengecualian khusus untuk Live Track
      if (isTemporaryLiveTrackPage() && isAuthError) {
        return
      }

      // Baru di sini log & toast (hanya akan jalan jika bukan halaman publik & bukan live-track)
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
      Vue.prototype.$mcToast.error(message ?? 'Something went wrong')

      // Redirect ke login jika auth error (kondisi ini otomatis hanya jalan untuk halaman non-publik)
      if (
        isAuthError &&
        !window.location.href.includes('/forgotpassword') &&
        !window.location.href.includes('/unsubscribe')
      ) {
        localStorage.removeItem('token')
        localStorage.removeItem('domainName')
        localStorage.removeItem('domainNameExpired')
        localStorage.removeItem('payload')
        window.location.href = '/login'
      }
    })
  }
  if (networkError) {
    const networkStatusCode = networkError?.statusCode ?? networkError?.status

    // 🔥 CEK TOKEN LANGSUNG DARI COOKIE
    const cookie = Cookie.getLocalCookie()
    const hasToken = cookie && cookie.resource

    // Jika belum login, DIAMKAN
    if (!hasToken) {
      return
    }

    // Jangan tampilkan error network di halaman live-track (aturan lama)
    if (isTemporaryLiveTrackPage() && [401, 500].includes(networkStatusCode)) {
      return
    }

    // Baru tampilkan error jika bukan halaman publik
    console.log(`[Network error]: ${networkError}`)
    Vue.prototype.$mcToast.error('Network error occurred')
  }
})

const pathName = window.location.pathname.split('/')
let authToken = null
let shortToken = null
function isBase64(str) {
  try {
    return btoa(atob(str)) == str
  } catch (err) {
    return false
  }
}
if (pathName.includes('live-track')) {
  var param = window.location.search
  const urlParams = new URLSearchParams(param)

  const token = urlParams.get('token')
  if (token) {
    authToken = token
  }
  const shortKey = urlParams.get('shToken')
  const id = parseInt(pathName[pathName.length - 1])

  shortToken = shortKey ? generateShortToken(id, shortKey) : null

  if (!shortToken && !authToken) {
    window.location.href = '/login'
  }
} else if (
  (pathName.includes('fleet-task') ||
    pathName.includes('fleet-task-instant') ||
    pathName.includes('delivery')) &&
  pathName.includes('track')
) {
  const url = window.location.href
  const parts = url.split('/')
  const target = parts[parts.length - 1]
  let decodedData
  if (isBase64(target)) {
    decodedData = atob(target)
  } else {
    const data = {
      hasButton: false,
    }
    localStorage.setItem('404', JSON.stringify(data))
    window.location.href = '/404'
  }
  const regex = /token=([^&]+)/
  const token = decodedData.match(regex)[1]
  const localCookie = Cookie.getLocalCookie()
  authToken = localCookie?.resource ? localCookie.resource : token
} else if (pathName.includes('public-tracking')) {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('auth')
  if (!token) {
    const data = {
      hasButton: false,
    }
    localStorage.setItem('404', JSON.stringify(data))
    window.location.href = '/404'
  }
  authToken = token
} else {
  const localCookie = Cookie.getLocalCookie()
  const feHost = Cookie.getMainDomain(window.location.host)
  const beHost = Cookie.getMainDomain(process.env.VUE_APP_API_URL)
  if (localCookie.resource && feHost != beHost) {
    authToken = localCookie.resource
  }
}

const headers = {
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
}
if (authToken) {
  headers.authorization = `Bearer ${authToken}`
}
if (shortToken) {
  headers.shorttoken = shortToken
}
const httpLink = new HttpLink({
  uri: process.env.VUE_APP_API_URL,
  credentials: 'include',
  headers:
    Object.keys(headers).length > 0
      ? headers
      : {
          priority: 'normal',
        },
  errorHandler,
})

export const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: errorHandler.concat(httpLink),
  fetchPolicy: 'no-cache',
})

const httpLinkLowPrio = new HttpLink({
  uri: process.env.VUE_APP_API_URL,
  credentials: 'include',
  headers:
    Object.keys(headers).length > 0
      ? headers
      : {
          priority: 'low',
        },
  errorHandler,
})

export const clientLowPrio = new ApolloClient({
  cache: new InMemoryCache(),
  link: errorHandler.concat(httpLinkLowPrio),
  fetchPolicy: 'no-cache',
})

const httpLinkRateLimit = new HttpLink({
  uri: process.env.VUE_APP_API_URL,
  credentials: 'include',
  headers:
    Object.keys(headers).length > 0
      ? headers
      : {
          priority: 'rate-limit',
        },
  errorHandler,
})

export const clientRateLimit = new ApolloClient({
  cache: new InMemoryCache(),
  link: errorHandler.concat(httpLinkRateLimit),
  fetchPolicy: 'no-cache',
})

// must set cookie resource on domain localhost
const httpProxyLink = new HttpLink({
  uri: '/proxy-graphql',
  credentials: 'include',
  headers:
    Object.keys(headers).length > 0
      ? headers
      : {
          priority: 'normal',
        },
  errorHandler,
})

export const clientProxy = new ApolloClient({
  cache: new InMemoryCache(),
  link: errorHandler.concat(httpProxyLink),
  fetchPolicy: 'no-cache',
})

/**
 * Check if URL needs proxy rewrite (ngrok, localhost, etc.)
 *
 * @param {string} url - The URL to check
 * @returns {boolean} True if URL is a valid proxy URL (ngrok)
 */
function isValidProxyUrl(url) {
  if (!url) return false
  return url.includes('ngrok-free.app')
}

// Development warning for proxy usage with ngrok
/* eslint-disable no-console */
if (
  process.env.VUE_APP_IS_USING_PROXY === 'true' &&
  process.env.VUE_APP_ENV === 'development' &&
  process.env.VUE_APP_API_GRAPHQL_PROXY_URL &&
  isValidProxyUrl(process.env.VUE_APP_API_GRAPHQL_PROXY_URL)
) {
  if (!authToken) {
    console.error('🚨 Proxy Error: No authentication token found!')
    console.error('📋 To use proxy with authentication:')
    console.error('   1. Login on your development site (e.g., .mceasy.cloud)')
    console.error(
      '   2. Open application tab and go to cookies tab with name http://localhost:8080'
    )
    console.error('   3. Copy name resource with its value and write domain localhost')
    console.error('   4. Refresh localhost to apply the token')
  }
}
/* eslint-enable no-console */
